import type React from "react"
import { useState, useEffect } from "react"
import {
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Stack,
  TextField,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Typography,
  Avatar,
  ListItemAvatar,
} from "@mui/material"
import { Add, Delete, Search, FileDownload, Refresh, PersonAdd, CheckCircle, Person } from "@mui/icons-material"
import * as XLSX from "xlsx"
import { fetchUsers, type User } from "../../utils/userAPI"
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Autocomplete, { AutocompleteRenderInputParams, AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import { GetLunchOptedUsers, OptLunch } from "../../utils/APIRequest";

const LunchOptedUser: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [allUsers, setAllUsers] = useState<User[]>([]); // for dialog selection
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]); // for add dialog
  const [selectedTableUsernames, setSelectedTableUsernames] = useState<string[]>([]); // for table selection
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  // Remove dialog state for delete, use simple confirmation dialog
  const [removeDialog, setRemoveDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({
    open: false,
    user: null,
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({
    open: false,
    message: "",
    severity: "success",
  })

  // Load lunch opted users on mount
  useEffect(() => {
    loadLunchOptedUsers();
    // Also fetch all users for dialog selection
    fetchUsers().then(setAllUsers);
  }, [])

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users)
    } else {
      const filtered = users.filter(
        (user) =>
          user.emp_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.username.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      setFilteredUsers(filtered)
    }
    setPage(0)
  }, [users, searchTerm])

  const loadLunchOptedUsers = async () => {
    setLoading(true);
    try {
      const result = await GetLunchOptedUsers();
      if (Array.isArray(result)) {
        setUsers(result);
      } else if (result && result.data && Array.isArray(result.data.users)) {
        setUsers(result.data.users);
      } else {
        setSnackbar({ open: true, message: (result && result.data && result.data.message) || 'Error loading lunch opted users', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error loading lunch opted users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }

  // Open dialog to add users to lunch poll
  const handleAddUser = () => {
    setAddDialogOpen(true);
  }
  // Save selected users as lunchOpted
  const handleAddToLunchPoll = async () => {
    setAdding(true);
    try {
      for (const username of selectedUsernames) {
        await OptLunch({ username, lunchOpted: true });
      }
      setSnackbar({ open: true, message: 'Users added to Lunch Poll', severity: 'success' });
      setAddDialogOpen(false);
      setSelectedUsernames([]);
      loadLunchOptedUsers();
    } catch (error) {
      setSnackbar({ open: true, message: 'Error adding users to Lunch Poll', severity: 'error' });
    } finally {
      setAdding(false);
    }
  }

  // Remove user from lunch poll (set lunchOpted to false)
  const handleRemoveUser = (user: User) => {
    setRemoveDialog({ open: true, user });
  };

  // Bulk remove dialog state
  const [bulkRemoveDialogOpen, setBulkRemoveDialogOpen] = useState(false);

  const handleConfirmRemove = async () => {
    if (removeDialog.user) {
      try {
        await OptLunch({ username: removeDialog.user.username, lunchOpted: false });
        setSnackbar({
          open: true,
          message: `User removed from Lunch`,
          severity: "success",
        });
        loadLunchOptedUsers();
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error removing user from Lunch',
          severity: "error",
        });
      }
    }
    setRemoveDialog({ open: false, user: null });
  };

  // Bulk remove handler
  const handleBulkRemove = () => {
    setBulkRemoveDialogOpen(true);
  };

  const handleConfirmBulkRemove = async () => {
    if (selectedTableUsernames.length > 0) {
      try {
        await Promise.all(selectedTableUsernames.map(username => OptLunch({ username, lunchOpted: false })));
        setSnackbar({
          open: true,
          message: `Selected users removed from Lunch`,
          severity: "success",
        });
        setSelectedTableUsernames([]);
        loadLunchOptedUsers();
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error removing selected users from Lunch',
          severity: "error",
        });
      }
    }
    setBulkRemoveDialogOpen(false);
  };

  const handleExportUsers = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredUsers.map((user) => ({
        Username: user.username,
        "Employee Name": user.emp_name,
      })),
    )

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users")
    XLSX.writeFile(workbook, `users_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Calculate pagination
  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Box>
      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search users by username or employee name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary Stats and Actions */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={3}>
          <Chip label={`Total Users: ${users.length}`} color="primary" variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" startIcon={<Refresh />} onClick={loadLunchOptedUsers} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={handleExportUsers}
            disabled={filteredUsers.length === 0}
          >
            Export
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddUser}>
            Add
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={handleBulkRemove}
            disabled={selectedTableUsernames.length === 0}
          >
            Remove
          </Button>
        </Stack>
      </Box>

      {/* Add to Lunch Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAdd />
          Select users to add to Lunch Poll
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ minHeight: 200 }}>
          <Autocomplete
            multiple
            id="user-selection"
            options={allUsers.filter((user) => !users.some((lu) => lu.username === user.username))}
            getOptionLabel={(option) => option.emp_name}
            value={allUsers.filter((user) => selectedUsernames.includes(user.username))}
            onChange={(event, newValue) => {
              setSelectedUsernames(newValue.map((user) => user.username))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search and select users"
                placeholder="Type to search users..."
                variant="outlined"
                fullWidth
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { onDelete, ...chipProps } = getTagProps({ index })
                return (
                  <Chip variant="filled" label={option.emp_name} color="primary" {...chipProps} key={option.username} />
                )
              })
            }
            renderOption={(props, option, { selected }) => (
              <Box component="li" {...props}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: selected ? "primary.main" : "grey.400", width: 32, height: 32 }}>
                    <Person fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={option.emp_name} secondary={option.username} />
                {selected && <CheckCircle color="primary" fontSize="small" />}
              </Box>
            )}
            sx={{
              "& .MuiAutocomplete-tag": {
                margin: "2px",
              },
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: "8px",
                paddingBottom: "8px",
              },
            }}
            noOptionsText="No users available"
            filterSelectedOptions
            disableCloseOnSelect
            limitTags={3}
            getLimitTagsText={(more) => `+${more} more`}
          />

          {selectedUsernames.length > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                {selectedUsernames.length} user{selectedUsernames.length !== 1 ? "s" : ""} selected
              </Typography>
            </Box>
          )}

        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setAddDialogOpen(false)} disabled={adding} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleAddToLunchPoll}
          variant="contained"
          color="primary"
          disabled={adding || selectedUsernames.length === 0}
          startIcon={<PersonAdd />}
        >
          {adding
            ? "Adding..."
            : "Add User"}
        </Button>
      </DialogActions>
    </Dialog>

      {/* Users Table with Bulk Selection */}
      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }} aria-label="users table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.light" }}>
              <TableCell padding="checkbox" sx={{ backgroundColor: 'primary.light' }}>
                <Checkbox
                  color="default"
                  sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '& .MuiSvgIcon-root': { color: 'white' } }}
                  indeterminate={selectedTableUsernames.length > 0 && selectedTableUsernames.length < paginatedUsers.length}
                  checked={paginatedUsers.length > 0 && selectedTableUsernames.length === paginatedUsers.length}
                  onChange={(_e, checked) => {
                    if (checked) {
                      setSelectedTableUsernames(paginatedUsers.map(u => u.username));
                    } else {
                      setSelectedTableUsernames([]);
                    }
                  }}
                  inputProps={{ 'aria-label': 'select all users' }}
                />
              </TableCell>
              <TableCell sx={{ color: "white" }}>Username</TableCell>
              <TableCell sx={{ color: "white" }}>Employee Name</TableCell>
              <TableCell sx={{ color: "white" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading users...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length > 0 ? (
              paginatedUsers.map((user) => (
                <TableRow key={user._id} hover selected={selectedTableUsernames.includes(user.username)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selectedTableUsernames.includes(user.username)}
                      onChange={(_e, checked) => {
                        if (checked) {
                          setSelectedTableUsernames(prev => [...prev, user.username]);
                        } else {
                          setSelectedTableUsernames(prev => prev.filter(u => u !== user.username));
                        }
                      }}
                      inputProps={{ 'aria-label': `select user ${user.username}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {user.username}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.emp_name}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Remove from Lunch">
                        <IconButton color="error" onClick={() => handleRemoveUser(user)} size="small">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? "No users found matching your search" : "No users found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Remove from Lunch Confirmation Dialog */}
      <Dialog open={removeDialog.open} onClose={() => setRemoveDialog({ open: false, user: null })}>
        <DialogTitle>Remove from Lunch</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove "{removeDialog.user?.emp_name}" from Lunch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialog({ open: false, user: null })} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Remove Confirmation Dialog */}
      <Dialog open={bulkRemoveDialogOpen} onClose={() => setBulkRemoveDialogOpen(false)}>
        <DialogTitle>Remove Selected Users from Lunch</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {selectedTableUsernames.length} selected user(s) from Lunch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRemoveDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmBulkRemove} color="error" variant="contained">Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default LunchOptedUser
